// The three panes a workload's page and a model's page share
// (02-DESIGN/04-workloads/04-placement-records.md § who reads, § the console
// surface): its output live and recent, its resources over time, its events —
// each the recent records paged from the service, then what arrives live on
// the records subject the role's template admits. Where a workload runs as
// several copies (decision 0035), each line and each event is marked with its
// copy, and the samples are drawn for one copy at a time.

import { useEffect, useState } from "react";

import { Button, bytes, Empty, NotAllowed, Pending, Pill, useLoad, when } from "../../ui/components.tsx";
import {
  heldBy,
  idSubject,
  PLACEMENT_HEADER,
  recordFilter,
  SERVICE,
  type Event,
  type Output,
  type Record_,
  type RecordKind,
  type RecordsReply,
  type RecordsRequest,
  type Sample,
  type State,
} from "../../wire/workloads.ts";
import { Table } from "../../ui/table.tsx";
import { useSurface } from "../surface.tsx";
import { SamplesChart } from "./Samples.tsx";
import { copyName, eventDetail, plainEvent, slotOfPlacement, slotsOf } from "./state.ts";

/** Shown is one record on the page, from the stream or live. */
interface Shown<T> {
  key: string;
  at: string;
  placement: string;
  body: T;
}

function fromPage<T>(r: Record_): Shown<T> {
  return { key: `s${r.seq}`, at: r.at, placement: r.placement, body: r.body as T };
}

/**
 * FOLLOW_EVERY_MS is how often a member's pane asks the records verb for what
 * is newer than what it shows: a member's template admits no subscription to
 * the records family, so the verb is how a member follows (04-placement-records.md
 * § who reads).
 */
const FOLLOW_EVERY_MS = 3000;

/**
 * useRecords reads the recent records of one kind and follows the newer ones:
 * an admin on the records family live, keeping only those the service wrote
 * or the executor holding the placement the record names, among the slots the
 * workload's get names (decisions 0025, 0035), a member through the records verb forward from the last
 * sequence shown, since the member template subscribes only its inbox; the
 * verb drops the rest itself.
 */
function useRecords<T extends { at: string }>(id: string, kind: RecordKind, limit: number, held: State) {
  const s = useSurface();
  const [recent, reload] = useLoad(`${id}/${kind}`, () =>
    s.bus.call<RecordsReply>(SERVICE, idSubject("records", id), { kind, limit } satisfies RecordsRequest),
  );
  const [live, setLive] = useState<Shown<T>[]>([]);
  const [liveErr, setLiveErr] = useState<unknown>(undefined);
  const bus = s.bus;
  const tenant = s.tenant;
  const subscribes = s.offered("workloads:list:any");
  // One string, so that a re-read naming the same holders keeps the subscription.
  const holders = slotsOf({ placement: held })
    .map((x) => `${x.placement ?? ""}=${x.executor ?? ""}`)
    .join(" ");
  useEffect(() => {
    const holds = new Map(holders.split(" ").map((h) => h.split("=") as [string, string]));
    if (tenant === "" || !subscribes) {
      return;
    }
    let n = 0;
    let off: (() => void) | undefined;
    try {
      off = bus.subscribe(
        recordFilter(tenant, id, kind),
        (m) => {
          const placement = m.header(PLACEMENT_HEADER) ?? "";
          if (!heldBy(m.subject, holds.get(placement) ?? "")) {
            return;
          }
          const body = m.json() as T;
          n++;
          setLive((prev) => [...prev.slice(-limit), { key: `l${n}`, at: body.at, placement, body }]);
        },
        (e) => {
          setLiveErr(e);
        },
      );
    } catch (e) {
      const failed: unknown = e;
      queueMicrotask(() => {
        setLiveErr(failed);
      });
    }
    return () => {
      off?.();
    };
  }, [bus, tenant, id, kind, limit, subscribes, holders]);
  useEffect(() => {
    if (subscribes || recent.state !== "ready") {
      return;
    }
    let from = (recent.value.records[0]?.seq ?? 0) + 1;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = () => {
      bus
        .call<RecordsReply>(SERVICE, idSubject("records", id), { kind, limit, from } satisfies RecordsRequest)
        .then(
          (reply) => {
            if (stopped) {
              return;
            }
            const got = reply.records;
            const last = got.at(-1);
            if (last !== undefined) {
              from = last.seq + 1;
              setLive((prev) => [...prev, ...got.map((r) => fromPage<T>(r))].slice(-limit));
            }
            timer = setTimeout(poll, reply.next !== undefined && reply.next > 0 ? 0 : FOLLOW_EVERY_MS);
          },
          (e: unknown) => {
            if (!stopped) {
              setLiveErr(e);
            }
          },
        );
    };
    timer = setTimeout(poll, FOLLOW_EVERY_MS);
    return () => {
      stopped = true;
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [bus, recent, id, kind, limit, subscribes]);
  const page: Shown<T>[] = recent.state === "ready" ? recent.value.records.map((r) => fromPage<T>(r)) : [];
  // The page comes newest first; the pane shows oldest first, then what arrived
  // after it. A member's polled records carry their sequence in the key, so one
  // the page already shows after a reload is shown once.
  const seen = new Set(page.map((r) => r.key));
  const all = [...page].reverse().concat(live.filter((r) => !seen.has(r.key)));
  return { recent, reload, all, liveErr };
}

/** PaneProps names the workload and its folded entry, as the workload's get answers it: its slots, their placements and executors. */
interface PaneProps {
  id: string;
  placement: State;
}

/** several reports whether a workload runs as more than one copy, so that its records are marked with theirs. */
function several(p: State): boolean {
  return (p.replicas ?? 0) > 1 || slotsOf({ placement: p }).length > 1;
}

/** copyOf names the copy a record's placement is held by now, or nothing for a placement no slot holds. */
function copyOf(p: State, placement: string): string {
  const slot = slotOfPlacement(p, placement);
  return slot === undefined ? "" : copyName(slot);
}

export function OutputPane({ id, placement }: PaneProps) {
  const { recent, reload, all, liveErr } = useRecords<Output>(id, "output", 300, placement);
  const marked = several(placement);
  return (
    <>
      {liveErr !== undefined && <NotAllowed err={liveErr} what="Following the output" />}
      {recent.state === "pending" && <Pending what="Reading the output" />}
      {recent.state === "refused" && <NotAllowed err={recent.err} what="The output" />}
      {recent.state === "ready" && all.length === 0 && (
        <Empty>No output yet. What it writes appears here as it is written.</Empty>
      )}
      {all.length > 0 && (
        <div className="log">
          {all.map((r) => (
            <div key={r.key} className={r.body.stream === "stderr" ? "stderr" : undefined}>
              <span className="at">{new Date(r.body.at).toLocaleTimeString()}</span>
              {marked && <span className="at">{copyOf(placement, r.placement) || r.placement}</span>}
              {r.body.line}
            </div>
          ))}
        </div>
      )}
      <div className="row" style={{ marginTop: 8 }}>
        <Button size="sm" kind="quiet" onClick={reload}>
          Reload the recent lines
        </Button>
      </div>
    </>
  );
}

export function SamplesPane({ id, placement }: PaneProps) {
  const { recent, all: every, liveErr } = useRecords<Sample>(id, "sample", 300, placement);
  // Samples are drawn per copy (04-placement-records.md § the console surface):
  // one copy's at a time, chosen where there are several.
  const slots = slotsOf({ placement });
  const marked = several(placement);
  const [slot, setSlot] = useState(0);
  const chosen = slots.find((x) => x.slot === slot)?.placement ?? "";
  const all = marked ? every.filter((r) => r.placement === chosen) : every;
  const last = all[all.length - 1];
  return (
    <>
      {marked && (
        <div className="row" style={{ marginBottom: 12 }} role="group" aria-label="Copy">
          {slots.map((x) => (
            <Button
              key={x.slot}
              size="sm"
              kind={x.slot === slot ? "default" : "quiet"}
              onClick={() => {
                setSlot(x.slot);
              }}
            >
              {copyName(x.slot)}
            </Button>
          ))}
        </div>
      )}
      {liveErr !== undefined && <NotAllowed err={liveErr} what="Following the resources" />}
      {recent.state === "pending" && <Pending what="Reading the resources" />}
      {recent.state === "refused" && <NotAllowed err={recent.err} what="The resources" />}
      {recent.state === "ready" && all.length === 0 && (
        <Empty>
          {marked
            ? `Nothing measured for ${copyName(slot)} yet. CPU and memory are sampled while it runs.`
            : "Nothing measured yet. CPU and memory are sampled while it runs."}
        </Empty>
      )}
      {all.length > 0 && (
        <div className="card stack">
          <SamplesChart samples={all.map((r) => r.body)} />
          {last !== undefined && (
            <dl className="kv small">
              <dt>Latest</dt>
              <dd>{when(last.body.at)}</dd>
              <dt>CPU</dt>
              <dd>
                {last.body.cpu_percent.toFixed(1)}% of {last.body.cpus}
              </dd>
              <dt>Memory</dt>
              <dd>
                {bytes(last.body.memory_bytes)} used, {bytes(last.body.memory_host_resident_bytes)} resident
                on the host
              </dd>
              <dt>Disk</dt>
              <dd>
                {bytes(last.body.disk_read_bytes)} read, {bytes(last.body.disk_write_bytes)} written
              </dd>
              <dt>Network</dt>
              <dd>
                {bytes(last.body.net_rx_bytes)} in, {bytes(last.body.net_tx_bytes)} out
              </dd>
              <dt>Up for</dt>
              <dd>{last.body.uptime_secs} s</dd>
            </dl>
          )}
        </div>
      )}
    </>
  );
}

export function EventsPane({ id, placement }: PaneProps) {
  const { recent, all, liveErr } = useRecords<Event>(id, "event", 100, placement);
  const marked = several(placement);
  return (
    <>
      {liveErr !== undefined && <NotAllowed err={liveErr} what="Following the events" />}
      {recent.state === "pending" && <Pending what="Reading the events" />}
      {recent.state === "refused" && <NotAllowed err={recent.err} what="The events" />}
      {recent.state === "ready" && all.length === 0 && (
        <Empty>No events yet. Starting, running, dying and stopping are listed here as they happen.</Empty>
      )}
      {all.length > 0 && (
        <Table
          label="Events"
          rows={all}
          rowKey={(r) => r.key}
          sort={{ key: "when", dir: "desc" }}
          columns={[
            {
              key: "when",
              header: "When",
              width: "150px",
              value: (r) => r.body.at,
              text: (r) => when(r.body.at),
              cell: (r) => <span className="small muted">{when(r.body.at)}</span>,
            },
            {
              key: "what",
              header: "What",
              width: "130px",
              facet: "Kind",
              value: (r) => plainEvent(r.body.event).word,
              cell: (r) => {
                const e = plainEvent(r.body.event);
                return <Pill tone={e.tone}>{e.word}</Pill>;
              },
            },
            {
              key: "id",
              header: "Id",
              narrow: false,
              value: (r) => (marked ? copyName(r.body.slot ?? 0) : r.placement),
              text: (r) => (marked ? `${copyName(r.body.slot ?? 0)} ${r.placement}` : r.placement),
              cell: (r) =>
                marked ? (
                  <span className="cell">
                    <span className="small">{copyName(r.body.slot ?? 0)}</span>
                    <span className="mono small muted">{r.placement}</span>
                  </span>
                ) : (
                  <span className="mono small muted">{r.placement}</span>
                ),
            },
            {
              key: "detail",
              header: "Detail",
              width: "minmax(0, 1.4fr)",
              narrow: false,
              sortable: false,
              value: (r) => eventDetail(r.body),
              cell: (r) => <span className="small muted">{eventDetail(r.body)}</span>,
            },
          ]}
        />
      )}
    </>
  );
}
